import { useQuery } from "@tanstack/react-query";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

import { INVOICES_PENDING_PATH } from "~/constants/appRoutes.ts";
import userViewGuard from "~/hooks/useViewPermssionsCheck";
import { invoiceStatusId } from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import { invoiceStatusQueries } from "~/modules/billing/api/invoiceStatus/invoiceStatusQueries.ts";
import { processedInvoiceQueries } from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import type {
	ValidInvoiceDetailsNavigatedFromOrigin,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import {
	validInvoiceDetailsNavigatedFromOrigins,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import InvoiceDetailsView from "~/modules/billing/components/InvoiceDetailsView/InvoiceDetailsView";
import { quotaQueries } from "~/modules/quota/api/quota/quotaQueries.ts";
import type { QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import { useAllUsers } from "~/modules/user/api/user/userQueries.ts";
import LoadingPage from "~/pages/LoadingPage.tsx";
import { PermissionNames } from "~/types/entityNames.ts";


const InvoiceDetailsPage: FunctionComponent = () => {
	const guard = userViewGuard([PermissionNames.CanManageInvoices, PermissionNames.CanViewInvoices, PermissionNames.CanManageProjects]);
	if (guard) return guard;

	const [searchParams] = useSearchParams();
	const fromParam = searchParams.get("from") as ValidInvoiceDetailsNavigatedFromOrigin;
	const { invoiceId } = useParams();
	// preload all users
	useAllUsers();

	const {
		data: invoiceStatusData,
		isLoading: invoiceStatusIsLoading,
	} = useQuery(invoiceStatusQueries.all());

	const {
		data: invoiceData,
		isLoading: invoiceIsLoading,
	} = useQuery(processedInvoiceQueries.details(invoiceId!));

	const quota = invoiceData ? invoiceData.quota : undefined;

	const {
		data: quotaDataWithRelations,
		isLoading: quotaIsLoading,
	} = useQuery(quotaQueries.details(quota?.companyId || "", quota?.id || "", true));

	const navigatedFromSanitised = useMemo(() => {
		if (fromParam) {
			if (Object.values(validInvoiceDetailsNavigatedFromOrigins).includes(fromParam)) {
				return fromParam;
			}
		}
		return undefined;
	}, [fromParam]);

	const isLoading = invoiceIsLoading || quotaIsLoading || invoiceStatusIsLoading;
	if (isLoading) return <LoadingPage />;

	if (invoiceData === undefined || !!quota && !quotaDataWithRelations || invoiceStatusData === undefined) {
		return null;
	}

	if (invoiceData.invoiceStatusId === invoiceStatusId.Pending) {
		return <Navigate to={INVOICES_PENDING_PATH} />;
	}

	return <InvoiceDetailsView invoiceData={invoiceData}
							   invoiceStatuses={invoiceStatusData}
							   navigatedFrom={navigatedFromSanitised}
							   quota={quotaDataWithRelations as QuotaWithRelations | undefined}
	/>;
};

export default InvoiceDetailsPage;